import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { NavController } from '@ionic/angular';
import { ConfigService } from '@smatchy/ionic-core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { JWTService } from '@smatchy/ionic-auth';
import {
  RefreshTokenAction,
  ResetAllAuthUserAction,
  ResetAuthErrorAction,
  ResetLoginInProgressAction,
  SendResetPwdAction,
  SetAccessTokenAction,
  SignInEmailPwdAction,
  SignInErrorAction,
  SignInProviderAction,
  SignUpEmailPwdAction,
  SignUpErrorAction,
  SignUpProviderAction,
  UserSignedAction,
} from './auth.actions';
import { FirebaseService } from '@smatchy/ionic-auth';
import { AuthService, ProfileService } from '@smatchy/sdk';
import { FirebaseError } from '@firebase/util';
import { AcceptedNewGcs } from '../../cgv/state/cgv.actions';
import { LoadUserProfileAction } from './user-profile.actions';
import { UserProfileSelector } from './user-profile.selector';
import { LanguageService } from '../../../services/language.service';
import { captureException } from '../../../../tracing';
import { DisconnectUserAction } from '../../app/state/app.actions';
import { theFirebaseAccountIsGone } from '../domain/the-firebase-account-is-gone';
import {
  TheFirebaseSessionNeverCameBack,
  theFirebaseSessionNeverCameBack,
} from '../domain/the-firebase-session-never-came-back';
import {
  RenewalTrigger,
  RESTORE_WAIT_MS,
  RestoreWait,
  storedTokenState,
  tokenRenewalFacts,
} from '../domain/token-renewal-facts';
import { tokenExpiry } from '../../../../token.interceptor';
import { PasswordResetService } from '../services/password-reset.service';
import { UsageMeasurementService } from '../../../services/usage-measurement.service';
import { EmailAlreadyUsedError } from '../errors/email-already-used.error';

export interface SignupProps {
  email: string;
  password: string;
  status: boolean;
}

export type AuthProviderType = 'google' | 'apple';

export enum SignupErrors {
  EMAIL_ALREADY_EXIST = 'email_already_exist',
  USER_PWD_NOT_FOUND = 'user_pwd_not_found',
  ERROR = 'error',
}

export enum SigninErrors {
  USER_PWD_NOT_FOUND = 'user_pwd_not_found',
  ERROR = 'error',
}

/**
 * What the identity provider told us about the person, which is not much.
 *
 * EVERY FIELD IS OPTIONAL, because Firebase answers null or nothing for each
 * of them: a Google or Apple account carries no display name unless the person
 * set one, an Apple private relay hides the address, and there is often no
 * picture. The type said five required strings and the code filled it from
 * `res.user?.displayName` and friends, so the absences were already arriving --
 * they were simply not declared. The onboarding screen exists to ask the person
 * for what is missing here.
 */
export type UserLogged = {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  photoUrl?: string;
};

export interface AuthStateModel {
  loginInProgress: boolean;
  resetPwdInProgress: boolean;
  /*
   * The four fields below are optional because the defaults beneath this set
   * every one of them to undefined: there is no token before a sign-in, no
   * sign-up in progress, and no error until something fails. The model said
   * they were always there.
   */
  accessToken?: string;

  signUp?: SignupProps;
  signUpError?: SignupErrors;
  signInError?: SigninErrors;
  provider: string;
  userLogged: UserLogged | undefined;
}

const defaults: AuthStateModel = {
  provider: '',
  signInError: undefined,
  signUp: undefined,
  signUpError: undefined,
  loginInProgress: false,
  accessToken: undefined,
  resetPwdInProgress: false,
  userLogged: undefined,
};

@State<AuthStateModel>({
  name: 'auth',
  defaults,
})
@Injectable()
export class AuthState {
  private readonly config: ConfigService = inject(ConfigService);
  private readonly usageMeasurement = inject(UsageMeasurementService);
  constructor(
    private readonly httpClient: HttpClient,
    private readonly _store: Store,
    private readonly _jwtService: JWTService,
    private readonly _router: Router,
    private readonly _alertController: AlertController,
    private readonly _translate: TranslateService,
    private readonly _firebaseService: FirebaseService,
    private readonly _authApiService: AuthService,
    private readonly _profileService: ProfileService,
    private readonly _navCtrl: NavController,
    private readonly _languageService: LanguageService,
    private readonly _passwordReset: PasswordResetService,
  ) {}

  /**
   * Renews the API token, and gives up on a session whose account no longer
   * exists.
   *
   * WHY GIVING UP IS THE RIGHT ANSWER HERE. CAPACITOR-R, in production: three
   * members whose Firebase account has been deleted still carry a session on
   * their phone. Every renewal asks Firebase about an account that is not
   * there, is told so, reports it and throws, and the app tries again a few
   * minutes later. CAPACITOR-D shows that loop from the outside, firing at
   * 09:06, 09:11, 09:16, 09:17 and 09:18 on one morning. Nothing in that loop
   * can ever succeed, and the member sits on a profile page that will never
   * load.
   *
   * Signing them out puts them on the login screen, where they can sign in
   * again and get a working account. It is the only exit.
   *
   * ONLY FOR AN ACCOUNT THAT IS GONE. Every other renewal failure is left
   * exactly as it was, and that restraint is the whole design: a network blip,
   * an expired token, the keychain restoring a moment late are all ordinary
   * and all recover by themselves. Signing somebody out for one of those would
   * take a session away from somebody who simply walked into a lift. See
   * domain/the-firebase-account-is-gone.ts, which recognises one situation and
   * refuses everything it has not seen before.
   *
   * NOT RETHROWN once the sign-out has run. The error has already been
   * reported by sayWhyTheRenewalFailed, and the member is on their way to the
   * login screen: putting a failure toast in front of somebody mid sign-out
   * describes a problem they can no longer do anything about.
   */
  @Action(RefreshTokenAction)
  async refreshTokenAction({ dispatch }: StateContext<AuthStateModel>, { trigger }: RefreshTokenAction) {
    let token: string;
    try {
      token = await this.refreshToken(trigger);
    } catch (error) {
      /*
       * TWO WAYS A SESSION ENDS, AND THEY END IT THE SAME WAY.
       *
       * The account is gone (CAPACITOR-R), or Firebase's session never came
       * back after the wait (CAPACITOR-D). Either way the application is
       * holding something it can never renew: the next renewal asks the same
       * question and gets the same answer, and when the API token finally
       * expires the member is stuck on a screen that will not load.
       *
       * Signing them out puts them on the login screen, where signing in
       * rebuilds the Firebase session. It is the only exit, and Maude took
       * that decision on 1 October 2026 after the tags showed `never-came`
       * was not a race.
       */
      if (theFirebaseAccountIsGone(error) || theFirebaseSessionNeverCameBack(error)) {
        await lastValueFrom(dispatch(new DisconnectUserAction()), { defaultValue: undefined });
        return;
      }

      /*
       * A RENEWAL AT LAUNCH THAT FAILS DOES NOT BREAK A WORKING SESSION.
       *
       * CAPACITOR-D, 27 members on the published 2.1.9. The diagnostic written
       * for this very question answered it on 1 October 2026, and the answer
       * is in four tags that have to be read together:
       *
       *   trigger      start       the renewal at launch, not a request
       *   restore_wait never-came  Firebase waited its three seconds and
       *                            still had nobody
       *   stored_token valid       OUR OWN API TOKEN IS STILL GOOD
       *   at_launch    true        in the first ten seconds
       *
       * So nothing is broken at that moment: the member has a working session
       * and the application throws at them anyway, because a renewal they did
       * not need failed. The renewal at launch is a precaution, and a
       * precaution that fails must not cost more than not taking it.
       *
       * THE REPORT IS NOT SILENCED. sayWhyTheRenewalFailed has already run, so
       * the event still reaches Sentry with its facts; only the throw stops.
       * That matters because this is not the end of the story: a Firebase
       * session that never comes back will fail the NEXT renewal too, the one
       * triggered by a request, and that one still throws and still recovers
       * through the 401 path. What changes is that the failure surfaces when
       * it actually blocks something.
       *
       * ONLY AT LAUNCH, ONLY WITH A VALID TOKEN. A renewal asked for by a
       * request is asked for because the token is no longer good enough, and
       * swallowing that one would leave the member on a screen waiting for
       * data that will never arrive.
       */
      if (trigger === 'start' && this.theSessionStillWorks()) {
        return;
      }

      throw error;
    }
    dispatch(new SetAccessTokenAction(token));
  }

  /**
   * Whether the API token on this device is still good.
   *
   * NEVER THROWS, and that is not a detail. This is read inside a catch, to
   * decide whether to swallow the error it caught. A failure here would
   * REPLACE that error with its own, which is the same mistake the renewal
   * diagnostic above was built to avoid. Anything unreadable answers no, and
   * the original failure travels on.
   */
  private theSessionStillWorks(): boolean {
    try {
      const token = this._jwtService.getAccessToken();

      return storedTokenState(token, token ? tokenExpiry(token) : null, Date.now()) === 'valid';
    } catch {
      return false;
    }
  }

  @Action(SetAccessTokenAction)
  async setAccessTokenAction({ patchState }: StateContext<AuthStateModel>, { token }: SetAccessTokenAction) {
    this._jwtService.setAccessToken(token);
    patchState({
      accessToken: token,
    });
  }

  /**
   * Creating an account with an email address, in one place and at one moment.
   *
   * IT USED TO CREATE NOTHING. This remembered the credentials and navigated to
   * /onboarding; the account, Firebase and our API both, was created at the end
   * of the onboarding form. Somebody who closed the app between the two screens
   * believed they had an account and had none: their address was free again,
   * their password was never registered, and nothing told them.
   *
   * Decided by Maude on 28 September 2026, from the two options written on the
   * ticket. The account exists as soon as the password is confirmed, and the
   * onboarding becomes what its name says -- a profile completed on an account
   * that already exists.
   *
   * WHAT THAT REMOVES, and it is the point as much as the fix: there is no
   * longer anything to remember between two screens, so the password is held
   * nowhere at all, not even in memory. PendingSignupService is gone with this,
   * and so is the exception the route guard needed for a half-created account.
   *
   * A failure here is a failure to create an account, and it happens on the
   * screen that holds the password -- so the person can read it, correct it and
   * try again, instead of meeting it three fields later.
   */
  @Action(SignUpEmailPwdAction)
  async signUpEmailPwdAction(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { email, password, acceptCgu }: SignUpEmailPwdAction,
  ) {
    patchState({
      loginInProgress: true,
    });
    try {
      const firebaseToken = await this.createOrOpenFirebaseAccount(email, password);
      dispatch(new SetAccessTokenAction(firebaseToken));

      const apiToken = await this.signUpApi('email', true);
      dispatch(new SetAccessTokenAction(apiToken));

      await this.ensureGcsAcceptedAfterEmailSignup(acceptCgu);

      patchState({
        loginInProgress: false,
      });
      await this._navCtrl.navigateForward('/onboarding');
    } catch (error) {
      // The button has to come back whatever happened, or the screen is as dead
      // as the one this replaces. The error is rethrown for the component, which
      // is the only place that knows how to say it to the person.
      patchState({
        loginInProgress: false,
      });
      throw error;
    }
  }

  /**
   * The Firebase side of the sign-up, and what to do when the address is taken.
   *
   * SMA-91: if the password opens the existing account, the person owns it and
   * is simply signed in. If it does not, they are told the address is taken and
   * offered to sign in, rather than being handed a generic failure.
   */
  private async createOrOpenFirebaseAccount(email: string, password: string): Promise<string> {
    let firebaseToken: string | undefined;
    try {
      firebaseToken = await this._firebaseService.signUp(email, password);
    } catch (err: unknown) {
      if (!this.isEmailAlreadyInUseError(err)) {
        throw err;
      }
      try {
        firebaseToken = await this._firebaseService.signInWithEmailAndPassword(email, password);
      } catch {
        throw new EmailAlreadyUsedError(email);
      }
    }
    if (!firebaseToken) {
      throw new Error('Firebase token not returned');
    }
    return firebaseToken;
  }

  @Action(SignInEmailPwdAction)
  async signInEmailPwdAction(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { email, password }: SignInEmailPwdAction,
  ) {
    patchState({
      loginInProgress: true,
    });
    try {
      const firebaseToken = await this._firebaseService.signInWithEmailAndPassword(email, password);
      if (firebaseToken) {
        dispatch(new SetAccessTokenAction(firebaseToken));
        const apiToken = await this.signUpApi('email');
        dispatch(new SetAccessTokenAction(apiToken));
        dispatch(new UserSignedAction('/home/explore'));
      } else {
        dispatch(new SignInErrorAction(SigninErrors.ERROR, 'Firebase token not returned'));
      }
    } catch (err: unknown) {
      console.error(err);
      if ((err as FirebaseError).code?.startsWith('auth/')) {
        dispatch(new SignInErrorAction(SigninErrors.USER_PWD_NOT_FOUND, (err as FirebaseError).code));
      } else {
        dispatch(new SignInErrorAction(SigninErrors.ERROR, (err as Error).message));
      }
    }
  }

  @Action(SignInProviderAction)
  async signInProviderAction(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { provider }: SignInProviderAction,
  ) {
    patchState({
      loginInProgress: true,
    });
    try {
      const firebaseToken = await this._firebaseService.signInWithProvider(provider);
      if (firebaseToken) {
        dispatch(new SetAccessTokenAction(firebaseToken));
        const apiToken = await this.signUpApi(provider);
        dispatch(new SetAccessTokenAction(apiToken));
        dispatch(new UserSignedAction('/home/explore'));
      } else {
        dispatch(new SignInErrorAction(SigninErrors.ERROR, 'Token not returned'));
      }
    } catch (err: unknown) {
      console.error(err);
      if (err instanceof FirebaseError && err.code?.startsWith('auth/')) {
        dispatch(new SignInErrorAction(SigninErrors.USER_PWD_NOT_FOUND, err.code));
      } else {
        dispatch(new SignInErrorAction(SigninErrors.ERROR, (err as Error).message));
      }
    }
  }

  @Action(SignUpProviderAction)
  async signUpProviderAction(
    { patchState, dispatch }: StateContext<AuthStateModel>,
    { provider }: SignInProviderAction,
  ) {
    patchState({
      loginInProgress: true,
    });
    try {
      const res = await this._firebaseService.signUpProvider(provider);
      const firstNameFromEmail = this.firstNameFromEmail(res.user?.email);
      if (res.token) {
        dispatch(new SetAccessTokenAction(res.token));
        const apiToken = await this.signUpApi(provider, true);
        dispatch(new SetAccessTokenAction(apiToken));

        if (firstNameFromEmail) {
          await firstValueFrom(
            this._profileService.updateProfile({
              updateMyProfileDto: {
                firstName: firstNameFromEmail,
                avatarUrl: res.user?.photoURL || undefined,
              },
            }),
          );
        }

        patchState({
          /*
           * `?? undefined` on each: Firebase answers null for a field it does
           * not have, and this state keeps one way of saying absent. Two
           * representations of nothing in the same object is what makes a
           * screen test for one and miss the other.
           */
          userLogged: {
            displayName: res.user?.displayName ?? undefined,
            firstName: firstNameFromEmail ?? res.user?.displayName?.split(' ')[0],
            lastName: res.user?.displayName?.split(' ')[1],
            email: res.user?.email ?? undefined,
            photoUrl: res.user?.photoURL ?? undefined,
          },
        });
        dispatch(new UserSignedAction('/home/explore'));
      } else {
        dispatch(new SignUpErrorAction(SignupErrors.ERROR, 'Token not returned'));
      }
    } catch (err: unknown) {
      if ((err as FirebaseError).code?.includes('auth/email-already-in-use')) {
        dispatch(new SignUpErrorAction(SignupErrors.EMAIL_ALREADY_EXIST, (err as FirebaseError).code));
      } else {
        dispatch(new SignUpErrorAction(SignupErrors.ERROR, (err as Error).message));
      }
    }
  }

  @Action(UserSignedAction)
  async userSignedAction({ patchState }: StateContext<AuthStateModel>, { redirectTo }: UserSignedAction) {
    const user = await this._firebaseService.getUser();
    if (user) {
      // Asked, not assumed: a device that refused the measurement keeps its
      // account id off the analytics SDK.
      void this.usageMeasurement.tellWhoThisIs(user.uid);
    }
    patchState({
      loginInProgress: false,
      // Same as above: one way of saying absent.
      userLogged: {
        displayName: user?.displayName ?? undefined,
        firstName: user?.displayName?.split(' ')[0],
        lastName: user?.displayName?.split(' ')[1],
        email: user?.email ?? undefined,
        photoUrl: user?.photoUrl ?? undefined,
      },
    });

    const deepLinkReturnUrl = sessionStorage.getItem('returnUrl');
    sessionStorage.removeItem('returnUrl');
    sessionStorage.setItem('skipLastRouteRestore', 'true');

    const targetUrl = deepLinkReturnUrl ?? redirectTo ?? '/home/explore';
    if (targetUrl.startsWith('/home')) {
      localStorage.setItem('lastRoute', targetUrl);
    }
    if (targetUrl === '/onboarding') {
      this._navCtrl.navigateForward(targetUrl);
      return;
    }
    this._navCtrl.navigateRoot(targetUrl);
  }

  @Action(ResetLoginInProgressAction)
  async resetLoginInProgressAction({ patchState }: StateContext<AuthStateModel>) {
    patchState({
      loginInProgress: false,
    });
  }

  @Action(SignInErrorAction)
  async signInErrorAction({ patchState }: StateContext<AuthStateModel>, action: SignInErrorAction) {
    console.error('Signin Error: ' + action.code, action);
    patchState({
      signInError: action.payload,
      loginInProgress: false,
    });
  }

  @Action(SignUpErrorAction)
  async signUpErrorAction({ patchState }: StateContext<AuthStateModel>, action: SignUpErrorAction) {
    console.error('Signup Error: ' + action.code, action);
    patchState({
      signUpError: action.payload,
      loginInProgress: false,
    });
  }

  @Action(ResetAuthErrorAction)
  async resetAuthErrorAction({ patchState }: StateContext<AuthStateModel>) {
    patchState({
      signUpError: undefined,
      signInError: undefined,
      loginInProgress: false,
    });
  }

  @Action(ResetAllAuthUserAction)
  async resetAllAuthUserAction() {
    this._jwtService.reset();
  }

  /**
   * The reset email now comes from our API, which mints the same Firebase
   * link with the Admin SDK and sends it through our own template. Firebase
   * used to compose it: plain text, unbranded, linking to firebaseapp.com.
   *
   * The API says the same thing whether or not the address has an account, so
   * the confirmation shown here says the same thing too. A failure it does
   * report is a failure of Firebase or of the mailer: the email is not on its
   * way, and saying "sent" would leave somebody waiting in front of an inbox
   * for a message that will never come.
   */
  @Action(SendResetPwdAction)
  async sendResetPwdAction({ patchState }: StateContext<AuthStateModel>, { email }: SendResetPwdAction) {
    patchState({
      resetPwdInProgress: true,
    });
    let sent = true;
    try {
      await firstValueFrom(this._passwordReset.send(email, this._languageService.getCurrentLang()));
    } catch (error) {
      sent = false;
      captureException(error);
    }
    patchState({
      resetPwdInProgress: false,
    });

    const alert = await this._alertController.create({
      header: this._translate.instant(sent ? 'AUTH.FORGOT_PWD.TITLE_EMAIL_SENT' : 'AUTH.FORGOT_PWD.ERROR_TITLE'),
      message: this._translate.instant(sent ? 'AUTH.FORGOT_PWD.EMAIL_SENT' : 'AUTH.FORGOT_PWD.ERROR'),
      buttons: ['Ok'],
    });

    await alert.present();

    alert.onDidDismiss().then(() => {
      if (sent) {
        this._router.navigate(['/auth/sign-in']);
      }
    });
  }

  private async ensureGcsAcceptedAfterEmailSignup(acceptCgu: boolean): Promise<void> {
    if (!acceptCgu) {
      return;
    }
    try {
      await firstValueFrom(this._store.dispatch(new LoadUserProfileAction()));
      let profile = this._store.selectSnapshot(UserProfileSelector.profile);
      const acceptedGcs = (profile as { acceptedGcs?: boolean } | undefined)?.acceptedGcs;
      if (profile?.userId && acceptedGcs !== true) {
        await firstValueFrom(this._store.dispatch(new AcceptedNewGcs(profile.userId)));
        await firstValueFrom(this._store.dispatch(new LoadUserProfileAction()));
        profile = this._store.selectSnapshot(UserProfileSelector.profile);
      }
    } catch (error) {
      // Reported, never rethrown. The account exists by this point and the
      // home-screen modal asks again, so blocking the sign-up would cost a
      // member to protect a record we can still obtain. What was missing was
      // knowing it happens at all: console.warn reaches nobody, and a consent
      // we cannot prove is a legal problem, not a logging one.
      captureException(error);
      console.warn('[Auth] auto-accept GCS after email signup failed', error);
    }
  }

  /**
   * Renews the API token, after giving Firebase the time to have a session.
   *
   * THE WAIT IS THE FIX FOR CAPACITOR-D AND CAPACITOR-V. Firebase restores its
   * user from the keychain asynchronously; asking for a token before that has
   * finished answers « no user is signed in », which on iOS arrives as
   * « RuntimeError erreur 1 » and sometimes as an empty token instead. The
   * renewal at launch runs right after the profile has loaded, so the API
   * session is demonstrably valid at that moment: the only thing missing was
   * Firebase, and only for an instant.
   *
   * IT COSTS NOTHING IN THE ORDINARY CASE. waitForRestoredUser returns at once
   * when somebody is already signed in, which is every launch but the slow
   * ones.
   *
   * AND IT MAKES THE REPORT CONCLUSIVE. When the wait runs out, « missing » no
   * longer means « perhaps too early »: Firebase had its three seconds. That
   * distinction travels with the event as restoreWait.
   */
  private async refreshToken(trigger: RenewalTrigger = 'request'): Promise<string> {
    const restoreWait = await this.waitForFirebase();

    let firebaseIdToken: string | undefined;
    try {
      firebaseIdToken = await this._firebaseService.getIdToken(true);
    } catch (error) {
      await this.sayWhyTheRenewalFailed(error, trigger, restoreWait);
      throw this.named(error, restoreWait);
    }
    if (!firebaseIdToken) {
      const empty = new Error('Firebase token not returned');
      await this.sayWhyTheRenewalFailed(empty, trigger, restoreWait);
      throw this.named(empty, restoreWait);
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const res = await firstValueFrom(this._authApiService.refresh({ refreshTokenDto: { firebaseIdToken } }));
    return res.access_token;
  }

  /**
   * Names a renewal failure when we know WHY, and leaves it alone otherwise.
   *
   * `never-came` is the only answer this promotes, and it is a measurement
   * rather than a reading of somebody's wording: we asked Firebase whether
   * anybody was signed in, gave it three seconds, and asked again. Everything
   * else travels exactly as the plugin raised it, so nothing downstream has to
   * learn a new shape it did not ask for.
   */
  private named(error: unknown, restoreWait: RestoreWait): unknown {
    return restoreWait === 'never-came' ? new TheFirebaseSessionNeverCameBack(error) : error;
  }

  /**
   * Reports WHY a renewal failed, and changes nothing about what happens next.
   *
   * CAPACITOR-D: four members on a store build got « RuntimeError erreur 1 »
   * from the iOS plugin, which on this path can only mean « No user is signed
   * in. » The message says no more than that, and three different causes
   * produce it. The facts gathered here tell them apart on the next
   * occurrence; see domain/token-renewal-facts.ts.
   *
   * THE ERROR IS STILL THROWN. This is a report, not a repair: catching the
   * failure here would silence the only signal there is without changing
   * anything for the member.
   *
   * IT NEVER THROWS ITSELF. Reading Firebase a second time is the most likely
   * thing to fail at exactly this moment, and a diagnostic that replaces the
   * error it was meant to describe is worse than no diagnostic.
   */
  /**
   * Waits for Firebase only when Firebase says there is nobody.
   *
   * Never throws: a plugin that cannot answer is exactly the moment a renewal
   * must still be attempted, and the failure reported honestly, rather than
   * replaced by a failure of the waiting itself.
   */
  private async waitForFirebase(): Promise<RestoreWait> {
    try {
      if (await this._firebaseService.getUser()) {
        return 'not-needed';
      }
      return (await this._firebaseService.waitForRestoredUser(RESTORE_WAIT_MS)) ? 'appeared' : 'never-came';
    } catch {
      return 'not-needed';
    }
  }

  private async sayWhyTheRenewalFailed(
    error: unknown,
    trigger: RenewalTrigger,
    restoreWait: RestoreWait,
  ): Promise<void> {
    try {
      let firebaseUser: 'present' | 'missing' | 'unreadable';
      try {
        firebaseUser = (await this._firebaseService.getUser()) ? 'present' : 'missing';
      } catch {
        firebaseUser = 'unreadable';
      }

      const storedToken = this._jwtService.getAccessToken();
      const facts = tokenRenewalFacts({
        trigger,
        firebaseUser,
        restoreWait,
        storedToken,
        expiresAt: storedToken ? tokenExpiry(storedToken) : null,
        // Milliseconds since the web view started. It does not reset when the
        // application comes back from the background, which is what makes a
        // small value meaningful.
        msSinceLaunch: typeof performance?.now === 'function' ? performance.now() : Number.MAX_SAFE_INTEGER,
      });

      /*
       * THE FACTS GO IN TAGS, NOT ONLY IN A CONTEXT, and that is the lesson of
       * CAPACITOR-D. This diagnostic was written to answer one question on the
       * next occurrence. The occurrence came, fifteen members of it, and the
       * answer could not be read: the context did not appear in any view
       * reachable from here, while the tag beside it did, from this very call.
       * Whether it is lost in transport or merely hidden by those views was
       * not worth another day to settle, because the conclusion is the same
       * either way. A diagnostic that can be read only one way is a diagnostic
       * that will be unreadable again.
       *
       * Tags are indexed, searchable and shown by every tool, and they turn
       * «how many of these were never-came» into a question anybody can answer
       * without opening a single event.
       *
       * NOTHING PERSONAL IS ADDED. Every value is one of a closed set: where
       * the renewal came from, whether Firebase had a user, whether waiting
       * helped, the STATE of the stored token and never the token itself, and
       * whether this happened in the first ten seconds after launch. The
       * context stays as well, for the numbers a tag should not carry.
       */
      captureException(error, {
        contexts: { token_renewal: { ...facts } },
        tags: {
          token_renewal: facts.firebaseUser,
          token_renewal_trigger: facts.trigger,
          token_renewal_restore_wait: facts.restoreWait,
          token_renewal_stored_token: facts.storedToken,
          token_renewal_at_launch: String(facts.withinFirstSeconds),
        },
      });
    } catch {
      // A report that fails is a report lost, never a session lost.
    }
  }

  private firstNameFromEmail(email: string | null | undefined): string | undefined {
    if (!email?.includes('@')) {
      return undefined;
    }
    const prefix = email.split('@')[0]?.trim();
    return prefix || undefined;
  }

  private isEmailAlreadyInUseError(err: unknown): boolean {
    const code = (err as FirebaseError)?.code ?? '';
    const message = (err as Error)?.message ?? '';
    return (
      code === 'auth/email-already-in-use' ||
      message.includes('email-already-in-use') ||
      message.toLowerCase().includes('already in use')
    );
  }

  private async signUpApi(provider: string, createNewAccountAfterDeletion = false): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const res = await firstValueFrom(
      this._authApiService.signUp({
        signUpMyProfileDto: {
          provider,
          createNewAccountAfterDeletion,
          language: this._languageService.getCurrentLang(),
        },
      }),
    );
    return res.access_token;
  }
}
